import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Users, Receipt, Megaphone, Wrench, CalendarRange, DoorOpen, LogOut, Sparkles, BedDouble, AlertTriangle
} from "lucide-react";

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/students", label: "Students", icon: Users },
  { to: "/admin/rooms", label: "Rooms & beds", icon: BedDouble },
  { to: "/admin/fees", label: "Fees", icon: Receipt },
  { to: "/admin/fines", label: "Fines", icon: AlertTriangle },
  { to: "/admin/notices", label: "Notices", icon: Megaphone },
  { to: "/admin/tickets", label: "Tickets", icon: Wrench },
  { to: "/admin/leaves", label: "Leaves & Day-out", icon: CalendarRange },
  { to: "/admin/visitors", label: "Visitor Log", icon: DoorOpen },
];

const studentNav = [
  { to: "/student", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/student/profile", label: "My Profile", icon: Users },
  { to: "/student/fees", label: "My Fees", icon: Receipt },
  { to: "/student/fines", label: "My Fines", icon: AlertTriangle },
  { to: "/student/notices", label: "Notices", icon: Megaphone },
  { to: "/student/tickets", label: "Maintenance Tickets", icon: Wrench },
  { to: "/student/leaves", label: "Leave / Day-out", icon: CalendarRange },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user.role === "admin" ? adminNav : studentNav;

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-cream flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-warm bg-white">
        <Link to={user.role === "admin" ? "/admin" : "/student"} className="px-6 pt-8 pb-6 flex items-center gap-2 group">
          <div className="h-9 w-9 rounded-md bg-terracotta flex items-center justify-center text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-heading text-2xl leading-none text-deep">Cozy Heaven</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A67B5B] mt-1">
              {user.role === "admin" ? "Admin Portal" : "Resident Portal"}
            </div>
          </div>
        </Link>
        <nav className="flex-1 px-3 pb-6 space-y-0.5">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s|\//g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#E8C2B8]/40 text-[#8C3F2C] font-medium"
                    : "text-[#75716E] hover:bg-[#F4F1EA] hover:text-deep"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-5 border-t border-warm">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#DCE3D6] text-[#4A5D3E] flex items-center justify-center text-sm font-medium">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-deep truncate" data-testid="user-name">{user.name}</div>
              <div className="text-xs text-warm truncate">{user.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full text-sm flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-warm text-deep hover:bg-[#F4F1EA] transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-warm z-40 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-terracotta flex items-center justify-center text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <span className="font-heading text-xl text-deep">Cozy Heaven</span>
        </div>
        <button onClick={handleLogout} data-testid="logout-btn-mobile" className="text-sm text-deep flex items-center gap-1">
          <LogOut className="h-4 w-4" />
        </button>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-warm z-40 flex justify-around py-2">
        {nav.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1 text-xs ${
                isActive ? "text-[#C86F58]" : "text-[#75716E]"
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            <span className="text-[10px]">{item.label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0 pb-20 md:pb-0">
        <div className="px-5 md:px-10 lg:px-14 py-8 md:py-10 max-w-[1500px] mx-auto animate-fade-up">
          {children}
        </div>
      </main>
    </div>
  );
}
